import HtmlElement from "../../../Elements/HtmlElement";
import {$body} from "../../../Constants";

const Timeline = (() => {
	const CLASS = {
		PERIOD_YEAR: 'history-period-years'
	};
	const SELECTOR = {
		CONTAINER: '.history-timeline',
		PERIOD: '.history-period',
		PERIOD_ELEMENT: '.history-period-element',
		PERIOD_YEAR: '.' + CLASS.PERIOD_YEAR,
		COLORED_SPINE: '.history-spine-colored'
	};

	// css class for animations on all elements below the viewport
	const INTERSECTION_CLASS = 'below-vp';
	// history-period-element-text top padding + 1/2 pseudo element height
	const ADDITIONAL_SPINE_OFFSET = 30;
	// inner window width at which the timeline is displayed in a single column
	const SINGLE_COLUMN_BREAKPOINT = 640;

	class Timeline extends HtmlElement {

		onReady() {
			// feature detection for Intersection observer
			if ('IntersectionObserver' in window) {
				// objects that will be observed for intersections and animated accordingly
				this.animatedObjects = this.$element.find(`${SELECTOR.PERIOD_YEAR}, ${SELECTOR.PERIOD_ELEMENT}`);
				// the colored part of the central spine that will be animated
				this.coloredSpine = this.$element.find(SELECTOR.COLORED_SPINE);

				const observer = this.setupIntersectionObserver();

				// skips boundingRect check if the last element was already below the viewport
				let belowViewport = false;
				this.animatedObjects.each((index, element) => {
					// hide elements below viewport
					if (belowViewport || element.getBoundingClientRect().top > window.innerHeight) {
						belowViewport = true;
						element.classList.add(INTERSECTION_CLASS);
					}
					// start observing these objects
					observer.observe(element);
				});
			}
		}

		/**
		 * Returns an intersection observer watching for intersections with the browser viewport
		 * @returns {IntersectionObserver}
		 */
		setupIntersectionObserver() {
			return new IntersectionObserver(
				(entries, observer) => this.intersection(entries, observer),
				{
					rootMargin: '0px',
					threshold: [0, 0.2]
				});
		}

		/**
		 * Handles objects intersecting with the viewport
		 * @param {IntersectionObserverEntry[]} entries
		 * @param {IntersectionObserver} observer
		 */
		intersection(entries, observer) {
			let additionalPeriodElementSpineOffset = ADDITIONAL_SPINE_OFFSET;

			for (let i = 0; i < entries.length; i++) {
				const entry = entries[i];

				// first run after page initialization, as IntersectionObserver callback triggers for all observed elements at this point
				if (!$(entry.target).data('observed')) {
					// do not add additional offset on first run, otherwise the colored spine will be too long
					additionalPeriodElementSpineOffset = 0;
					// remember that this element has been through the first run
					$(entry.target).data('observed', true);
					// exclude objects below viewport during first run
					if ($(entry.target).hasClass(INTERSECTION_CLASS)) {
						continue;
					}
				}

				// is element below viewport?
				if (entry.boundingClientRect.top >= entry.rootBounds.top) {
					// find elements index in array of observed objects
					const elementIndex = this.animatedObjects.toArray().findIndex(element => element === entry.target);

					// hide elements again if they are scrolled below the viewport
					if (entry.intersectionRatio < 0.2) {
						entry.target.classList.add(INTERSECTION_CLASS);

						// move colored spine next to the previous object that is still visible
						const prevElement = this.animatedObjects[elementIndex - 1];
						if (prevElement) {
							this.calculateColoredSpineHeight(prevElement, elementIndex, 0);
						}
					}

					// show element when at least 20% of it have been scrolled into the viewport from below
					if (entry.intersectionRatio >= 0.2) {
						entry.target.classList.remove(INTERSECTION_CLASS);

						this.calculateColoredSpineHeight(entry.target, elementIndex, additionalPeriodElementSpineOffset);
					}
				}
			}
		}

		/**
		 * Calculates position (height) for the colored part of the central spine element
		 * @param {HTMLElement} element the element the spine will be positioned next to
		 * @param {int} elementIndex the index of element in the array of all observed objects
		 * @param {int} additionalPeriodElementSpineOffset
		 */
		calculateColoredSpineHeight(element, elementIndex, additionalPeriodElementSpineOffset) {
			const
				containerBoundingRect = this.$element[0].getBoundingClientRect(),
				periodYearsPaddingTop = parseInt(this.$element.find(SELECTOR.PERIOD_YEAR).first().css('padding-top'));

			if (element.classList.contains(CLASS.PERIOD_YEAR)) {
				// calculate spine height when last element is a year marker
				this.coloredSpine.height(element.getBoundingClientRect().top - containerBoundingRect.top + periodYearsPaddingTop);
			} else {
				// calculate spine height when last element is a content box (.history-period-element)
				if (elementIndex === this.animatedObjects.length - 1) {
					// last object; move colored spine to the very end
					this.coloredSpine.height('100%');
				} else {
					if (window.innerWidth < SINGLE_COLUMN_BREAKPOINT) {
						// calculate spine height to the top if the current elements bounding box
						this.coloredSpine.height(
							element.getBoundingClientRect().top - containerBoundingRect.top
						);
					} else {
						// calculate spine height to the position of the pseudo element attached to the current element
						this.coloredSpine.height(
							element.getBoundingClientRect().top - containerBoundingRect.top
							+ parseInt(window.getComputedStyle(element, ':before').top)
							- additionalPeriodElementSpineOffset
						);
					}
				}
			}
		}
	}

	return Timeline._getElement($body.find(SELECTOR.CONTAINER), Timeline);
})();

export default Timeline;
